import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Send, Zap, AlertCircle, CheckCircle2, Loader2, GitBranch } from "lucide-react";
import { PageHead, SectionHead } from "@/components/SectionHead";
import { EmptyState } from "@/components/EmptyState";
import { KpiCard, KpiGrid } from "@/components/dashboard/KpiCard";
import { format } from "date-fns";

// Supported lifecycle events (kept in sync with backend safe_emit call sites)
const LIFECYCLE_EVENTS = [
  { key: "contact.created",   label: "Contact created",  trigger: "New CRM contact added",                              wired: true  },
  { key: "interaction.logged", label: "Interaction logged", trigger: "Call / meeting / note logged on a contact, or a catalog inquiry submitted", wired: true },
  { key: "order.placed",       label: "Order placed",      trigger: "New order created",                                   wired: true },
  { key: "order.dispatched",   label: "Order dispatched",  trigger: "Production job hits ready_dispatched milestone",       wired: true },
  { key: "visit.checked_in",   label: "Field visit checked-in", trigger: "Salesperson checks in for a manual field visit",  wired: true },
  { key: "payment.received",   label: "Payment received",  trigger: "Reserved — will fire when the payments module ships",  wired: false },
];

export default function EnchargePage() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testEvent, setTestEvent] = useState("contact.created");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, st, ev] = await Promise.all([
      api.get("/encharge/settings").catch(() => ({ data: null })),
      api.get("/encharge/stats").catch(() => ({ data: null })),
      api.get("/encharge/events?limit=25").catch(() => ({ data: { items: [] } })),
    ]);
    setSettings(s.data);
    setStats(st.data);
    setEvents(ev.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.patch("/encharge/settings", { from_email: settings.from_email, from_name: settings.from_name, enabled: settings.enabled });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!testTo) return toast.error("Enter a recipient");
    setBusy(true);
    try {
      const { data } = await api.post("/encharge/test", { to_email: testTo, event_name: testEvent, properties: { source: "manual-test" } });
      toast.success(`Event ${data.status} · mode=${data.mode}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const mode = settings?.mode || "mock";
  const isMock = mode === "mock";

  return (
    <div className="space-y-6" data-testid="encharge-page">
      <PageHead
        eyebrow="Automation · Email"
        title="Encharge"
        subtitle="Lifecycle events + transactional email flows."
        actions={
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${isMock ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-[#E1F0E6] text-[#0F5E39] border border-[#0F5E39]/30"}`} data-testid="encharge-mode-badge">
            {isMock ? <><AlertCircle size={12}/> Mock mode — set ENCHARGE_API_KEY to go live</> : <><CheckCircle2 size={12}/> Live</>}
          </div>
        }
      />

      {isMock && (
        <Card className="border-amber-200 bg-amber-50/40 rounded-2xl">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm text-amber-900">
              <div className="font-medium mb-1">Encharge is running in mock mode.</div>
              All events are captured and shown below with status <b>mocked</b>. Paste your Encharge API key below to go live — no restart needed.
              Get the token from Encharge → Settings → API Keys.
            </div>
            <ConnectWizard onDone={load}/>
          </CardContent>
        </Card>
      )}
      {!isMock && settings?.source === "tenant" && (
        <Card className="border-[#0F5E39]/30 bg-[#E1F0E6]/40 rounded-2xl">
          <CardContent className="p-3 flex items-center gap-3 text-sm text-[#0F5E39]">
            <CheckCircle2 size={16}/>
            <div className="flex-1">Encharge connected via tenant key. Events are shipped live.</div>
            <Button size="sm" variant="outline" className="rounded-full border-[#0F5E39] text-[#0F5E39] hover:bg-[#0F5E39]/10"
              onClick={async () => { await api.post("/encharge/disconnect"); toast.success("Disconnected"); load(); }}
              data-testid="encharge-disconnect-btn"
            >
              Disconnect
            </Button>
          </CardContent>
        </Card>
      )}

      <KpiGrid cols={4}>
        <KpiCard label="Total events" value={stats?.total ?? "—"} icon={Zap} tone="green" testId="kpi-total"/>
        <KpiCard label="Sent"         value={stats?.sent ?? 0}    icon={CheckCircle2} tone="green" testId="kpi-sent"/>
        <KpiCard label="Mocked"       value={stats?.mocked ?? 0}  icon={Mail} tone="amber" testId="kpi-mocked"/>
        <KpiCard label="Failed"       value={stats?.failed ?? 0}  icon={AlertCircle} tone="red" testId="kpi-failed"/>
      </KpiGrid>

      {/* Settings */}
      {settings && (
        <Card className="border-[#E4DFCF] rounded-2xl">
          <CardContent className="p-5 space-y-3">
            <SectionHead title="Settings" subtitle="Sender identity used for transactional templates."/>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>From email</Label>
                <Input value={settings.from_email || ""} onChange={(e)=>setSettings({...settings, from_email: e.target.value})} className="mt-1 h-11" placeholder="hello@shreetaru.in" data-testid="encharge-from-email"/>
              </div>
              <div>
                <Label>From name</Label>
                <Input value={settings.from_name || ""} onChange={(e)=>setSettings({...settings, from_name: e.target.value})} className="mt-1 h-11" placeholder="TARU · Shree Taru Jewels"/>
              </div>
              <div className="flex items-end">
                <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6" onClick={saveSettings} data-testid="encharge-save-btn">Save</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test send */}
      <Card className="border-[#E4DFCF] rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <SectionHead title="Test event" subtitle="Fire a sample event. Runs live if key is set, else mocked."/>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Recipient</Label>
              <Input type="email" value={testTo} onChange={(e)=>setTestTo(e.target.value)} placeholder="you@company.in" className="mt-1 h-11" data-testid="encharge-test-to"/>
            </div>
            <div>
              <Label>Event name</Label>
              <Select value={testEvent} onValueChange={setTestEvent}>
                <SelectTrigger className="mt-1 h-11" data-testid="encharge-test-event"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {LIFECYCLE_EVENTS.map(e => <SelectItem key={e.key} value={e.key}>{e.label} · <span className="text-[#4A5A50]">{e.key}</span></SelectItem>)}
                  <SelectItem value="custom.event">custom.event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6" onClick={send} disabled={busy} data-testid="encharge-test-send">
                {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Send size={14} className="mr-1"/>}
                Send test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supported lifecycle events */}
      <Card className="border-[#E4DFCF] rounded-2xl">
        <CardContent className="p-5">
          <SectionHead title="Lifecycle events" subtitle="These events are wired inside the app and fire automatically."/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {LIFECYCLE_EVENTS.map(e => (
              <div key={e.key} className={`border rounded-xl p-3 ${e.wired ? "border-[#0F5E39]/25 bg-[#E1F0E6]/25" : "border-[#E4DFCF] bg-[#F6F1E1]/40"}`} data-testid={`lifecycle-${e.key}`}>
                <div className="flex items-center gap-2">
                  <GitBranch size={14} className={e.wired ? "text-[#0F5E39]" : "text-[#4A5A50]"}/>
                  <div className="font-medium text-sm">{e.label}</div>
                  <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${e.wired ? "bg-[#0F5E39] text-white" : "bg-[#F1EDDF] text-[#4A5A50]"}`}>{e.wired ? "wired" : "pending"}</span>
                </div>
                <div className="text-[11px] text-[#4A5A50] mt-1 font-mono">{e.key}</div>
                <div className="text-xs text-[#4A5A50] mt-1">{e.trigger}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent events */}
      <SectionHead title="Recent events" subtitle="Latest 25."/>
      {events.length === 0 ? (
        <EmptyState icon={Mail} eyebrow="No events" title="No email traffic yet" description="Fire a test event above, or let the app enqueue lifecycle events automatically."/>
      ) : (
        <Card className="border-[#E4DFCF] rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F6F1E1]/60 text-[10px] uppercase tracking-widest text-[#4A5A50]">
                  <th className="p-3 text-left">When</th>
                  <th className="p-3 text-left">Event</th>
                  <th className="p-3 text-left">To</th>
                  <th className="p-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-t border-[#E4DFCF]">
                    <td className="p-3 text-xs">{e.created_at ? format(new Date(e.created_at), "d MMM HH:mm") : "—"}</td>
                    <td className="p-3">{e.event_name}</td>
                    <td className="p-3 text-xs">{e.to_email || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${e.status==="sent"?"bg-[#E1F0E6] text-[#0F5E39]":e.status==="mocked"?"bg-amber-50 text-amber-700":e.status==="failed"?"bg-red-50 text-red-700":"bg-[#F1EDDF] text-[#4A5A50]"}`}>{e.status}</span>
                      {e.error && <div className="text-[10px] text-red-600 mt-1 max-w-xs truncate" title={e.error}>{e.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function ConnectWizard({ onDone }) {
  const [key, setKey] = React.useState("");
  const [fromEmail, setFromEmail] = React.useState("");
  const [fromName, setFromName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (!key.trim()) { toast.warning("Paste an API key first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/encharge/connect", {
        api_key: key.trim(), from_email: fromEmail.trim() || null, from_name: fromName.trim() || null,
      });
      toast.success(`Connected · reached Encharge ${data.reachable_status || "(unknown)"}`);
      setKey(""); setFromEmail(""); setFromName("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Connection failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="encharge-connect-wizard">
      <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="ENCHARGE_API_KEY" type="password"
             className="md:col-span-2 h-11 bg-white" data-testid="encharge-api-key-input"/>
      <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="From email (optional)" className="h-11 bg-white"/>
      <Button className="rounded-full bg-[#0C6E3D] hover:bg-[#08331E] text-white h-11 px-6"
              disabled={busy || !key.trim()} onClick={submit} data-testid="encharge-connect-btn">
        {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Zap size={14} className="mr-2"/>}
        Connect
      </Button>
    </div>
  );
}

